import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import type { Rahmen } from './Projektrahmen';
import { ebeneMitHaus } from '../mehrhaus';
import type {
  ApiClient, Raum, Raumfeld, RaumbuchDaten, ExcelVorschau, RaumGrundriss,
  RaumbuchAuftragStand, RaumbuchDauer,
} from '../api';
import { Fachaktionen } from './Fachaktionen';
import { Legende, farbklasse } from '../Farbregel';
import {
  IconEbene, IconRaum, IconTabelle, IconHochladen, IconSuche, IconBuch, GruppenIcon,
} from '../Icons';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, ContentSection, DataTable, Drawer, EmptyState, ErrorState,
  Feld as MusterFeld, KpiCard, KpiReihe, LoadingState, StatusBadge,
  formatiereZahl, leseZahl, type Spalte,
} from '../muster';

/**
 * Digitales Raumbuch.
 *
 * Links die Gliederung nach Geschoss und Raum, rechts der gewaehlte Raum mit
 * allen Werten nach Fachgruppen geordnet.
 *
 * Eine Tabelle waere bei sechzig Feldern nicht lesbar - man saehe entweder
 * wenige Spalten oder muesste endlos scrollen. Die Detailansicht zeigt alles,
 * was zu einem Raum gehoert, an einer Stelle.
 */
export function Raumbuch({ api }: { api: ApiClient }) {
  const { id: projektId } = useParams<{ id: string }>();
  // F60 H2b: die Haus-Wahl des Projektrahmens (null = alle; ohne Rahmen bzw. bei einem Haus immer null).
  const haus = useOutletContext<Rahmen | null | undefined>()?.haus ?? null;

  const [daten, setDaten] = useState<RaumbuchDaten | null>(null);
  const [felder, setFelder] = useState<Raumfeld[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const [offeneEbenen, setOffeneEbenen] = useState<Set<string>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [suche, setSuche] = useState('');

  const laden = useCallback(async () => {
    if (!projektId) return;
    try {
      // Ohne Haus-Wahl genau der bisherige Aufruf (ein Argument).
      const [d, f] = await Promise.all([
        haus == null ? api.raeume(projektId) : api.raeume(projektId, haus),
        api.raumfelder(),
      ]);
      setDaten(d);
      setFelder(f);
      setFehler(null);

      // Beim ersten Laden die erste Ebene aufklappen - eine leere Navigation
      // sieht aus, als waere nichts da.
      setOffeneEbenen((bisher) => {
        if (bisher.size > 0 || d.raeume.length === 0) return bisher;
        const r = d.raeume.find((x) => x.state !== 'entfernt');
        const erste = r?.level
          ? ebeneMitHaus(r.level, r.hausNummer, d.mehrhaus === true) : undefined;
        return erste ? new Set([erste]) : bisher;
      });
    } catch (e) {
      setFehler(e);
    }
  }, [api, projektId, haus]);

  useEffect(() => { laden(); }, [laden]);

  // Nach Geschoss gruppieren. Die Reihenfolge kommt aus der Aufnahme und
  // entspricht der im Modell.
  const ebenen = useMemo(() => {
    if (!daten) return [];

    const treffer = suche.trim().toLowerCase();
    const gruppen = new Map<string, Raum[]>();

    for (const r of daten.raeume) {
      if (r.state === 'entfernt') continue;

      if (treffer) {
        const passt = r.number.toLowerCase().includes(treffer)
                   || r.name.toLowerCase().includes(treffer);
        if (!passt) continue;
      }

      // F60 H2b: im Mehrhausprojekt „Haus 02 · E0“ - Haus vor Geschoss, wie in Tabelle und PDF.
      const ebene = ebeneMitHaus(r.level ?? 'ohne Geschoss', r.hausNummer, daten.mehrhaus === true);
      if (!gruppen.has(ebene)) gruppen.set(ebene, []);
      gruppen.get(ebene)!.push(r);
    }

    return [...gruppen.entries()]
      .map(([name, raeume]) => ({
        name,
        raeume: raeume.sort((a, b) => a.number.localeCompare(b.number, 'de')),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [daten, suche]);

  const raum = daten?.raeume.find((r) => r.id === gewaehlt) ?? null;

  const kopf = (
    <PageHeader
      titel="Raumbuch"
      beschreibung={'Links die Gliederung nach Geschoss und Raum, rechts der gewählte '
        + 'Raum mit allen Werten nach Fachgruppen geordnet. Eine Tabelle wäre bei '
        + 'sechzig Feldern nicht lesbar.'}
      meta={daten && (
        <>{daten.raeume.filter((r) => r.state !== 'entfernt').length} Räume
          {felder ? ` · ${felder.length} Felder` : ''}</>
      )}
    />
  );

  if (fehler && (!daten || !felder)) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Das Raumbuch konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!daten || !felder) {
    return (
      <>
        {kopf}
        <LoadingState text="Räume werden geladen …" zeilen={8} spalten={3} />
      </>
    );
  }

  if (daten.raeume.length === 0) {
    return (
      <>
        {kopf}
        <Fachaktionen api={api} rolleKey="raumbuch" leseArt={10} schreibArt={11} />
        <EmptyState
          icon={<IconRaum />}
          titel="Noch keine Räume aufgenommen."
          erklaerung={'Die Räume entstehen aus einem Auftrag mit Raumaufnahme. Gelesen '
            + 'werden die MEP-Räume des Modells.'}
        />
      </>
    );
  }

  return (
    <>
      {kopf}

      {/* Lesen und Schreiben im Kopf, Excel als weitere Knoepfe daneben - so
          liegt alles, was den ganzen Bestand betrifft, an einer Stelle. */}
      <Fachaktionen api={api} rolleKey="raumbuch" leseArt={10} schreibArt={11}>
        <Excelbereich api={api} projektId={projektId!} neuLaden={laden} />
        <GewerkUnterlagenZip api={api} projektId={projektId!} gewerk="raeume" />
      </Fachaktionen>

      <Kennzahlen raeume={daten.raeume} felder={felder} />

      {/* Links die Gliederung, rechts der gewählte Raum - dasselbe Muster wie
          Heizung und Kälte, und über die gemeinsamen Klassen .baum,
          .baum-ebene und .baum-zeile. Vorher war die Navigation eine eigene
          Karte mit von Hand gesetzten Farbwerten: ein festes Hellblau für die
          gewählte Zeile und zwei Grautöne für die Trennlinien. Sie folgten
          keiner Themenvariablen und wichen von jeder anderen Seite ab. */}
      <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
        <Navigation
          ebenen={ebenen}
          offen={offeneEbenen}
          umschalten={(e) => {
            const neu = new Set(offeneEbenen);
            if (neu.has(e)) neu.delete(e); else neu.add(e);
            setOffeneEbenen(neu);
          }}
          gewaehlt={gewaehlt}
          waehlen={setGewaehlt}
          suche={suche}
          setSuche={setSuche}
          gesamt={daten.raeume.filter((r) => r.state !== 'entfernt').length}
        />

        <div style={{ flex: 1, padding: '12px 16px', minWidth: 0 }}>
          {raum
            ? <Raumdetail key={raum.id} api={api} projektId={projektId!}
                          raum={raum} felder={felder} neuLaden={laden}
                          mehrhaus={daten.mehrhaus === true} />
            : (
              <EmptyState
                icon={<IconRaum />}
                titel="Kein Raum gewählt."
                erklaerung={'Links ein Geschoss aufklappen und einen Raum wählen — '
                  + 'seine Werte erscheinen dann hier.'}
              />
            )}
        </div>
      </div>
    </>
  );
}

// --- Kennzahlen --------------------------------------------------------------

/**
 * Die Zahlen über dem Baum.
 *
 * Der Füllgrad ist die eigentliche Auskunft des Raumbuchs: 1.021 Räume mit
 * sechzig Feldern sind 61.260 Zellen, und wie viele davon stehen, sieht man an
 * keinem einzelnen Raum.
 *
 * Die doppelten Nummern stehen daneben, weil sie sonst niemand findet. Am
 * Projekt 8000 tragen 11 Räume 5 Nummern - die Rangblöcke, gleiche Nummer,
 * verschiedene Namen und Flächen, gleiches Geschoss. Die Heizlast schlüsselt
 * über die RaumUniqueId und bleibt davon unberührt, der Melderbedarf aber über
 * die RAUMNUMMER: Dort bekäme jeder der drei LT-BLK-03 die Melder aller drei
 * zugezählt. Heute hängt in keinem von ihnen ein Melder - der Fehler ist
 * angelegt und nicht eingetreten, und genau deshalb gehört er auf das Blatt.
 */
function Kennzahlen({ raeume, felder }: { raeume: Raum[]; felder: Raumfeld[] }) {
  const z = useMemo(() => {
    const lebend = raeume.filter((r) => r.state !== 'entfernt');

    const flaeche = lebend.reduce((s, r) => s + (r.area ?? 0), 0);
    // felder kommt schon gefiltert aus der API - room_fields.IsActive wird
    // dort ausgewertet, die Schnittstelle führt die Spalte nicht.
    const aktive = felder;

    let gefuellt = 0;

    for (const r of lebend) {
      if (!r.dataJson) continue;

      let werte: Record<string, unknown>;
      try { werte = JSON.parse(r.dataJson); } catch { continue }

      for (const f of aktive) {
        const w = werte[f.fieldKey];
        if (w !== undefined && w !== null && String(w).trim() !== '') gefuellt++;
      }
    }

    const zellen = lebend.length * aktive.length;

    // Doppelte Nummern - über die Nummer geschlüsselt, wie es der
    // Melderbedarf tut.
    const jeNummer = new Map<string, number>();
    for (const r of lebend)
      jeNummer.set(r.number, (jeNummer.get(r.number) ?? 0) + 1);

    const doppelt = [...jeNummer.entries()].filter(([, n]) => n > 1);

    return {
      raeume: lebend.length,
      neu: lebend.filter((r) => r.state === 'neu').length,
      flaeche,
      felder: aktive.length,
      fuellgrad: zellen > 0 ? 100 * gefuellt / zellen : 0,
      doppelteNummern: doppelt.length,
      doppelteRaeume: doppelt.reduce((s, [, n]) => s + n, 0),
    };
  }, [raeume, felder]);

  return (
    <KpiReihe>
      <KpiCard
        titel="Räume" wert={z.raeume}
        vergleich={z.neu > 0 ? `${z.neu} neu` : undefined}
      />
      <KpiCard titel="Fläche" wert={Math.round(z.flaeche)} einheit="m²" />
      <KpiCard
        titel="Füllgrad" wert={z.fuellgrad} nachkomma={0} einheit="%"
        vergleich={`über ${z.felder} Felder`}
        zustand={z.fuellgrad < 50 ? 'warnung' : undefined}
        zustandText={z.fuellgrad < 50 ? 'weniger als die Hälfte gepflegt' : undefined}
      />
      <KpiCard
        titel="Doppelte Nummern" wert={z.doppelteNummern}
        vergleich={z.doppelteNummern > 0
          ? `${z.doppelteRaeume} Räume betroffen` : undefined}
        zustand={z.doppelteNummern > 0 ? 'warnung' : undefined}
        zustandText={z.doppelteNummern > 0 ? 'über die Nummer geschlüsselt' : undefined}
        fussnote={'Der Melderbedarf schlüsselt über die RAUMNUMMER — dort bekäme '
          + 'jeder Raum die Melder aller gleichnamigen zugezählt.'}
      />
    </KpiReihe>
  );
}

// --- Navigation --------------------------------------------------------------

function Navigation({
  ebenen, offen, umschalten, gewaehlt, waehlen, suche, setSuche, gesamt,
}: {
  ebenen: { name: string; raeume: Raum[] }[];
  offen: Set<string>;
  umschalten: (ebene: string) => void;
  gewaehlt: string | null;
  waehlen: (id: string) => void;
  suche: string;
  setSuche: (s: string) => void;
  gesamt: number;
}) {
  return (
    <div className="baum">
      <div style={{ padding: '10px 12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconSuche size={14} />
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Nummer oder Name"
            style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
          />
        </label>
        <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 6 }}>
          {gesamt.toLocaleString('de-DE')} Räume
        </div>
      </div>

      {ebenen.map((e) => (
        <div key={e.name}>
          <button className="baum-ebene" onClick={() => umschalten(e.name)}>
            <IconEbene size={14} />
            <span>{e.name}</span>
            <span className="schwach">{e.raeume.length}</span>
          </button>

          {offen.has(e.name) && e.raeume.map((r) => (
            <button
              key={r.id}
              className={`baum-zeile ${gewaehlt === r.id ? 'aktiv' : ''}`}
              onClick={() => waehlen(r.id)}
              title={`${r.number} — ${r.name}`}
            >
              <IconRaum size={13} />

              {/* Nummer und Name nebeneinander. "E1-042" allein sagt
                  niemandem etwas; wer Daten pflegt, sucht den Namen. */}
              <span style={{ minWidth: 0, overflow: 'hidden',
                             textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <strong>{r.number}</strong>
                <span className="schwach" style={{ marginLeft: 5 }}>{r.name}</span>
              </span>

              {r.state === 'neu' && (
                <span className="marke laufend" style={{ marginLeft: 'auto',
                                                         fontSize: 10 }}>
                  neu
                </span>
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

// --- Raumdetail ---------------------------------------------------------------

/** Der Text einer 409-Antwort beim Speichern (TR1), sonst `null`. */
function konfliktText(fehler: unknown): string | null {
  return fehler instanceof Error && fehler.message.startsWith('Inzwischen geändert')
    ? fehler.message
    : null;
}

/** Exportiert fuer den Seitentest des Speicherns (raumbuch-speichern.test.tsx, TR1). */
export function Raumdetail({
  api, projektId, raum, felder, neuLaden, mehrhaus = false,
}: {
  api: ApiClient;
  projektId: string;
  raum: Raum;
  felder: Raumfeld[];
  neuLaden: () => void;
  /** F60 H2b: im Mehrhausprojekt steht das Haus vor dem Geschoss. */
  mehrhaus?: boolean;
}) {
  const ausDb: Record<string, string | null> = raum.dataJson ? JSON.parse(raum.dataJson) : {};
  // Je FieldKey: hand | modell | abgeleitet | standard. Fehlt der Schluessel,
  // gilt das Feld als 'modell' (Konvention seit Migration 0464).
  const herkunftJeFeld: Record<string, string> =
    raum.herkunftJson ? JSON.parse(raum.herkunftJson) : {};
  const raumart = ausDb['nutzung'] ?? undefined;

  const [werte, setWerte] = useState(ausDb);
  const [gespeichert, setGespeichert] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);
  // Die Felder, die hier von Hand geändert wurden (TR1). Kommt der Raum neu
  // geladen zurück — nach dem Speichern oder nach einem Konflikt —, bleiben
  // nur sie stehen; alle anderen zeigen wieder den Stand der Datenbank. Sonst
  // schickte das nächste Speichern alte Werte für Felder, die inzwischen die
  // Raumbuchableitung geändert hat.
  const [bearbeitet, setBearbeitet] = useState<Set<string>>(new Set());

  useEffect(() => {
    const neu: Record<string, string | null> = raum.dataJson ? JSON.parse(raum.dataJson) : {};
    setWerte((bisher) => {
      const zusammen = { ...neu };
      for (const k of bearbeitet) zusammen[k] = bisher[k] ?? null;
      return zusammen;
    });
    // Nur beim neuen Stand aus der Datenbank, nicht bei jeder Eingabe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raum.dataJson]);

  // Ungespeicherte Aenderungen erkennen, damit die Schaltflaeche nur dann
  // aktiv ist - und sichtbar wird, dass etwas aussteht.
  const geaendert = useMemo(() => {
    const alle = new Set([...Object.keys(ausDb), ...Object.keys(werte)]);
    for (const k of alle) {
      if ((ausDb[k] ?? '') !== (werte[k] ?? '')) return true;
    }
    return false;
  }, [ausDb, werte]);

  const gruppen = useMemo(() => {
    const map = new Map<string, Raumfeld[]>();
    for (const f of felder) {
      const g = f.gruppe ?? 'Sonstige';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(f);
    }
    return [...map.entries()];
  }, [felder]);

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    // Nur was sich gegenüber dem geladenen Stand geändert hat, und zu jedem
    // Feld der Wert, den diese Seite gesehen hat (TR1). Hat jemand anderes —
    // etwa die Raumbuchableitung — dasselbe Feld inzwischen geändert, lehnt
    // der Server ab, statt den neuen Wert still zurückzuschreiben.
    const geaenderteWerte: Record<string, string | null> = {};
    const gesehen: Record<string, string | null> = {};
    for (const k of new Set([...Object.keys(ausDb), ...Object.keys(werte)])) {
      if ((ausDb[k] ?? '') !== (werte[k] ?? '')) {
        geaenderteWerte[k] = werte[k] ?? null;
        gesehen[k] = ausDb[k] ?? null;
      }
    }
    try {
      await api.raumSpeichern(raum.id, geaenderteWerte, gesehen);
      setBearbeitet(new Set());
      setGespeichert(true);
      setTimeout(() => setGespeichert(false), 2500);
      neuLaden();
    } catch (e) {
      setFehler(e);
      // Den heutigen Stand holen: Die eigenen Eingaben bleiben stehen, die
      // übrigen Felder zeigen, was inzwischen gilt.
      neuLaden();
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div>
      <div className="karte" style={{ position: 'sticky', top: 16, zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 21, fontWeight: 300, margin: 0 }}>{raum.number}</h1>
          <span>{raum.name}</span>
          <span className="sehr-schwach">
            {mehrhaus ? ebeneMitHaus(raum.level ?? '', raum.hausNummer, true) : raum.level} · {raum.area?.toFixed(1)} m²
            {raum.volume ? ` · ${raum.volume.toFixed(1)} m³` : ''}
          </span>

          {raum.state === 'neu' && <StatusBadge zustand="info" text="neu" klein />}
          {!raum.arcRoomGuid && (
            <StatusBadge
              zustand="warnung" text="ohne ARC-Bezug" klein
              titel="Kein Architekturraum an dieser Stelle gefunden"
            />
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {gespeichert && <StatusBadge zustand="gut" text="gespeichert" klein />}
            <button className="m-knopf m-knopf-haupt" onClick={speichern}
                    disabled={!geaendert || laeuft}>
              {laeuft ? 'wird gespeichert …' : 'Speichern'}
            </button>
          </div>
        </div>

        {fehler !== null && (
          // Ein Konflikt (409, TR1) ist keine Störung, sondern eine Auskunft:
          // Sein Text sagt, welches Feld inzwischen anders steht. Er steht
          // deshalb als Meldung da und nicht im zugeklappten technischen Teil.
          konfliktText(fehler)
            ? <ErrorState klein meldung={konfliktText(fehler)!} />
            : <ErrorState klein meldung="Der Raum konnte nicht gespeichert werden."
                          technisch={fehler} />
        )}
      </div>

      <Legende mitHerkunft />

      <Grundrisskarte api={api} projektId={projektId} raum={raum} />

      {gruppen.map(([gruppe, gruppenfelder]) => (
        <ContentSection
          key={gruppe}
          titel={gruppe}
          werkzeuge={<GruppenIcon gruppe={gruppe} />}
          aufklappbar
        >
          <div className="m-feldgitter m-feldgitter-3">
            {gruppenfelder.map((f) => (
              <Raumfeldeingabe
                key={f.id}
                feld={f}
                wert={werte[f.fieldKey] ?? ''}
                herkunft={herkunftJeFeld[f.fieldKey] ?? 'modell'}
                raumart={raumart}
                setzen={(v) => {
                  setWerte({ ...werte, [f.fieldKey]: v || null });
                  setBearbeitet(new Set(bearbeitet).add(f.fieldKey));
                }}
              />
            ))}
          </div>
        </ContentSection>
      ))}

      <p className="sehr-schwach" style={{ textAlign: 'right' }}>
        Aufgenommen {new Date(raum.firstSeenAt).toLocaleDateString('de-DE')},
        zuletzt gesehen {new Date(raum.lastSeenAt).toLocaleDateString('de-DE')}
      </p>
    </div>
  );
}


// --- Grundriss ----------------------------------------------------------------

/**
 * Der Raumgrundriss im Raumbuch.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DAS SVG EINGEBETTET WIRD UND NICHT IN EINEM <img> STEHT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die MSR-Schemen holen ihr SVG als Blob und haengen es in ein `<img>`. Hier
 * ginge dabei beides verloren, was den Grundriss im Raumbuch ausmacht: Die
 * `<title>`-Elemente je Bauteil sind in einem Bild tot (kein Hover), und eine
 * CSS-Regel dieser Seite erreicht das Dokument im Bild nicht (kein
 * Gewerkeumschalter). Eingebettet erreicht beides sein Ziel - und der
 * Umschalter braucht keinen zweiten Abruf.
 *
 * Der Zeichner stellt jede Gewerkgruppe in ein `<g class="gw-…">`; die Klassen
 * kommen aus DERSELBEN Liste, aus der die API ihre Gewerke meldet. Die Regeln
 * unten blenden die abgewaehlten aus.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ZWEI FLAECHEN SIND KEIN FEHLER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Steht `flaechenStreit`, weichen die Flaeche des Modells und die aus dem
 * Polygon um mehr als zwei Prozent voneinander ab. Dann stehen BEIDE da, mit
 * Herkunft. Eine einzige Zahl waere die bequemere Anzeige und verbaerge genau
 * den Fall, der geprueft gehoert - 80 von 927 Raeumen dieses Bauwerks liegen
 * darueber.
 */
export function Grundrisskarte({
  api, projektId, raum,
}: {
  api: ApiClient;
  projektId: string;
  raum: Raum;
}) {
  const [svg, setSvg] = useState<string | null>(null);
  const [daten, setDaten] = useState<RaumGrundriss | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [ausgeblendet, setAusgeblendet] = useState<Set<string>>(new Set());

  useEffect(() => {
    let lebt = true;

    setSvg(null);
    setDaten(null);
    setFehler(null);
    setAusgeblendet(new Set());

    Promise.all([
      api.raumGrundrissSvg(projektId, raum.id),
      api.raumGrundriss(projektId, raum.id),
    ])
      .then(([bild, zahlen]) => {
        if (!lebt) return;
        setSvg(bild);
        setDaten(zahlen);
      })
      .catch((e) => { if (lebt) setFehler(e); });

    return () => { lebt = false; };
  }, [api, projektId, raum.id]);

  const umschalten = (gewerk: string) => {
    const neu = new Set(ausgeblendet);
    if (neu.has(gewerk)) neu.delete(gewerk); else neu.add(gewerk);
    setAusgeblendet(neu);
  };

  const regeln = [...ausgeblendet]
    .map((g) => `.grundriss-bild .gw-${g}{display:none}`)
    .join('\n');

  return (
    <ContentSection
      titel="Grundriss"
      aufklappbar
      meta={daten
        ? (daten.hatUmriss
            ? `Maßstab 1 : ${daten.massstab}`
              + (daten.massstabIstNorm ? '' : ' (eingepasst)')
            : 'ohne Umriss')
        : undefined}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Der Grundriss konnte nicht geladen werden."
                    technisch={fehler} />
      )}

      {fehler === null && svg === null && <LoadingState text="Grundriss wird abgeleitet …" />}

      {daten && !daten.hatUmriss && (
        <p className="sehr-schwach" style={{ marginTop: 0 }}>
          {daten.befundText}
        </p>
      )}

      {daten && daten.hatUmriss && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {daten.gewerke.map((g) => (
            <button
              key={g.gewerk}
              type="button"
              className="m-knopf"
              aria-pressed={!ausgeblendet.has(g.gewerk)}
              onClick={() => umschalten(g.gewerk)}
              title={g.mitRaumnummerFremd > 0
                ? `${g.mitRaumnummerFremd} Bauteil(e) tragen diese Raumnummer, `
                  + 'liegen aber außerhalb des Umrisses — sie werden nicht gezeichnet.'
                : undefined}
              style={{
                opacity: ausgeblendet.has(g.gewerk) ? 0.45 : 1,
                borderLeft: `4px solid ${g.farbe}`,
              }}
            >
              {g.bezeichnung} {g.imPolygon}
              {g.darstellung === 'punkt' && ' ·  Punkte'}
              {g.darstellung === 'nurgezaehlt' && ' ·  zu dicht'}
              {g.mitRaumnummerFremd > 0 && ` · +${g.mitRaumnummerFremd} außerhalb`}
            </button>
          ))}
        </div>
      )}

      {svg && (
        <>
          <style>{regeln}</style>
          <div
            className="grundriss-bild"
            style={{ width: '100%', overflow: 'hidden' }}
            // Der SVG-Text kommt aus der eigenen API und wird dort erzeugt,
            // nicht von einem Nutzer eingegeben.
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        </>
      )}

      {daten && daten.hatUmriss && (
        <div className="sehr-schwach" style={{ marginTop: 8, lineHeight: 1.6 }}>
          {daten.flaechenStreit ? (
            <div>
              <strong>Zwei Flächen:</strong>{' '}
              {daten.flaecheModellM2?.toFixed(2)} m² aus dem Modell gegen{' '}
              {daten.flaechePolygonM2?.toFixed(2)} m² aus dem Polygon
              {' '}({daten.abweichungProzent?.toFixed(1)} %). Sie weichen um mehr
              als {daten.streitgrenze} % voneinander ab; auf dem Blatt stehen
              deshalb beide.
            </div>
          ) : (
            <div>
              Fläche {daten.flaecheModellM2?.toFixed(2)} m² — Modell und
              Polygon sind sich einig (≤ {daten.streitgrenze} %).
            </div>
          )}

          <div>
            {daten.achsen.length} Achse(n) im Bild
            {daten.achsenUnterdrueckt > 0
              && `, ${daten.achsenUnterdrueckt} weitere schneiden den Ausschnitt `
                 + 'und sind weggelassen'}
            {' · '}{daten.tueren} Tür(en), {daten.fenster} Fenster
            {' · '}Stand {daten.stand}
          </div>

          {daten.sehnenSegmente > 0 && (
            <div>
              {daten.sehnenSegmente} gebogene Wandsegment(e) sind als Sehne
              gezeichnet — die Raumbegrenzung führt nur Anfang und Ende.
              Größte Überlänge {daten.sehnenUeberlaengeM.toFixed(2)} m.
            </div>
          )}
        </div>
      )}
    </ContentSection>
  );
}

/**
 * Ein Feld des Raumbuchs.
 *
 * Heisst seit U6a `Raumfeldeingabe` und nicht mehr `Feld`: Das Muster
 * `Feld` aus `muster/` traegt denselben Namen, und zwei gleichnamige
 * Komponenten in einer Datei sind eine Falle. Die HERKUNFTSFARBE bleibt: Sie
 * ist fachliche Kennzeichnung und kein Zierrat.
 */
function Raumfeldeingabe({
  feld, wert, herkunft, raumart, setzen,
}: {
  feld: Raumfeld;
  wert: string;
  /** hand | modell | abgeleitet | standard - fehlt der Aufrufer, gilt 'modell'. */
  herkunft?: string;
  /** Fuer den Tooltip bei Herkunft 'standard' - der Modus gilt je Raumart. */
  raumart?: string;
  setzen: (v: string) => void;
}) {
  // Hellgruen: Das Feld geht beim Rueckschreiben ins Modell zurueck.
  // Hellgelb: Der Wert bleibt in der Datenbank.
  //
  // Hier stand bis PB52 `feld.revitParameter ? 'modellfeld' : 'datenfeld'`.
  // Das Raumbuch lag damit als einzige der drei Seiten richtig - und zwar
  // zufaellig: Die Saat aus Migration 0723 bildet genau diese Regel ab, und
  // PB50 hat room_fields nicht angefasst. Eine einzige kuenftige Festlegung
  // des Fachplaners haette die Seite stillschweigend falsch gemacht, so wie
  // 0729 das Tuerbuch. Jetzt kommt die Antwort fertig aus dem Rechenkern
  // (Rueckschreibweg.GehtInsModell) - dieselbe, die auch der Lauf gibt, der
  // wirklich schreibt.
  //
  // Der WERT sticht das FELD: Hat die Raumbuchableitung den Wert selbst
  // errechnet ('abgeleitet') oder als Standard der Raumart gesetzt
  // ('standard'), gilt dieselbe Farbe wie bei einem reinen Datenbankfeld -
  // auch wenn das Feld selbst zurueckgeschrieben wird (dann noch NICHT
  // geschrieben, siehe Fachaktion "Ins Modell schreiben").
  const abgeleitetOderStandard = herkunft === 'abgeleitet' || herkunft === 'standard';
  const art = abgeleitetOderStandard
    ? 'wert-abgeleitet'
    : farbklasse(feld.gehtInsModell);

  const herkunftTitel = herkunft === 'standard'
    ? `Standardwert der Raumart${raumart ? ` '${raumart}'` : ''} - häufigster Wert, nicht von Hand geprüft`
    : herkunft === 'abgeleitet'
      ? 'Abgeleitet aus den aufgenommenen Gewerkebeständen - nicht von Hand geprüft'
      : undefined;

  // Wie im Tuerbuch: Der Parametername allein sagt nicht, ob der Wert dort
  // ankommt. Er beantwortet "woher kommt der Wert", nicht "geht er zurueck".
  const hinweis = herkunftTitel
    ?? (feld.gehtInsModell
      ? `${feld.revitParameter} — wird ins Modell zurückgeschrieben`
      : feld.revitParameter
        ? `${feld.revitParameter} — wird nur gelesen, nicht zurückgeschrieben`
        : 'Nur im Raumbuch - kein Parameter im Modell hinterlegt');

  const beschriftung = feld.unit ? `${feld.label} [${feld.unit}]` : feld.label;

  // Ja/Nein kommt aus Revit als TRUE/FALSE - eine Auswahl ist hier lesbarer
  // als ein Freitextfeld, in dem jemand "ja" schreibt.
  if (feld.dataType === 'ja_nein') {
    return (
      <div className={art} title={herkunftTitel}>
        <MusterFeld
          art="auswahl"
          beschriftung={beschriftung}
          hinweis={hinweis}
          wert={wert}
          beiAenderung={(w) => setzen(w as string)}
          optionen={[
            { wert: '', beschriftung: '—' },
            { wert: 'TRUE', beschriftung: 'ja' },
            { wert: 'FALSE', beschriftung: 'nein' },
          ]}
        />
      </div>
    );
  }

  if (feld.dataType === 'auswahl' && feld.options) {
    return (
      <div className={art} title={herkunftTitel}>
        <MusterFeld
          art="auswahl"
          beschriftung={beschriftung}
          hinweis={hinweis}
          wert={wert}
          beiAenderung={(w) => setzen(w as string)}
          optionen={[
            { wert: '', beschriftung: '—' },
            ...feld.options.split(',').map((o) => ({
              wert: o.trim(), beschriftung: o.trim(),
            })),
          ]}
        />
      </div>
    );
  }

  /* Abschnitt 15: Wert und Einheit getrennt. Gespeichert wird weiterhin ein
     Text; `leseZahl`/`formatiereZahl` uebersetzen an der Grenze. */
  if (feld.dataType === 'zahl') {
    return (
      <div className={art} title={herkunftTitel}>
        <MusterFeld
          art="zahl"
          beschriftung={feld.label}
          einheit={feld.unit ?? undefined}
          hinweis={hinweis}
          wert={leseZahl(wert)}
          beiAenderung={(w) => setzen(w === null ? '' : formatiereZahl(w as number))}
        />
      </div>
    );
  }

  return (
    <div className={art} title={herkunftTitel}>
      <MusterFeld
        beschriftung={beschriftung}
        hinweis={hinweis}
        wert={wert}
        beiAenderung={(w) => setzen(w as string)}
      />
    </div>
  );
}


// --- Excel ---------------------------------------------------------------

/**
 * Aus- und Eingabe als Tabelle.
 *
 * Beim Einlesen wird zuerst nur gezeigt, was sich aendern wuerde. Ohne diesen
 * Schritt waere es ein Sprung ins Dunkle: Die Datei war zwischenzeitlich in
 * fremden Haenden, jemand hat vielleicht eine Spalte verschoben oder in ein
 * Zahlenfeld "ca. 1800" geschrieben.
 */
function Excelbereich({
  api, projektId, neuLaden,
}: {
  api: ApiClient;
  projektId: string;
  neuLaden: () => void;
}) {
  const [datei, setDatei] = useState<File | null>(null);
  const [pdfDialog, setPdfDialog] = useState(false);
  const [vorschau, setVorschau] = useState<ExcelVorschau | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const pruefen = async (f: File) => {
    setLaeuft(true);
    setFehler(null);
    setDatei(f);
    try {
      const ergebnis = await api.raumbuchEinlesen(projektId, f, false);
      setVorschau(ergebnis.vorschau);
    } catch (e) {
      setFehler(e);
      setDatei(null);
    } finally {
      setLaeuft(false);
    }
  };

  const uebernehmen = async () => {
    if (!datei) return;
    setLaeuft(true);
    try {
      await api.raumbuchEinlesen(projektId, datei, true);
      setVorschau(null);
      setDatei(null);
      neuLaden();
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  const aenderungsSpalten: Spalte<ExcelVorschau['aenderungen'][number]>[] = [
    {
      schluessel: 'raum', titel: 'Raum', breite: '110px', immerSichtbar: true,
      wert: (a2) => a2.nummer,
      inhalt: (a2) => <strong>{a2.nummer}</strong>,
    },
    { schluessel: 'feld', titel: 'Feld', wert: (a2) => a2.feld },
    {
      schluessel: 'alt', titel: 'bisher',
      wert: (a2) => a2.alt ?? '',
      inhalt: (a2) => <span className="sehr-schwach">{a2.alt ?? '—'}</span>,
    },
    {
      schluessel: 'neu', titel: 'neu',
      wert: (a2) => a2.neu ?? '',
      inhalt: (a2) => a2.neu ?? <span className="sehr-schwach">gelöscht</span>,
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          className="m-knopf"
          onClick={() => setPdfDialog(true)}
          disabled={laeuft}
          title="Ein Blatt je Raum, zur Übergabe"
        >
          <IconBuch size={16} />Als PDF
        </button>

        <button
          className="m-knopf"
          onClick={() => api.raumbuchExcel(projektId, 'Raumbuch.xlsx')}
          disabled={laeuft}
        >
          <IconTabelle size={16} />Als Tabelle
        </button>

        <label className="m-knopf" style={{ cursor: 'pointer' }}>
          <IconHochladen size={16} />Tabelle einlesen
          <input
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) pruefen(f);
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {fehler !== null && (
        <ErrorState
          meldung="Die Tabelle konnte nicht gelesen werden."
          technisch={fehler}
          aktionen={(
            <button className="m-knopf" onClick={() => setFehler(null)}>Schließen</button>
          )}
        />
      )}

      {pdfDialog && (
        <PdfDialog
          api={api}
          projektId={projektId}
          schliessen={() => setPdfDialog(false)}
        />
      )}

      {/* Die Vorschau im Schubfach statt im winzigen Modal (Abschnitt 16):
          Sie zeigt eine Tabelle, und die war vorher bei 60 Zeilen
          abgeschnitten — `DataTable` virtualisiert stattdessen. */}
      <Drawer
        offen={vorschau !== null}
        titel="Was sich ändern würde"
        beschreibung={vorschau
          ? `${vorschau.zeilenGelesen} Zeilen gelesen · ${vorschau.aenderungen.length} Änderungen`
          : undefined}
        breite="860px"
        beiSchliessen={() => { setVorschau(null); setDatei(null); }}
        fuss={vorschau ? (
          <>
            <button className="m-knopf"
                    onClick={() => { setVorschau(null); setDatei(null); }}>
              Abbrechen
            </button>
            <button
              className="m-knopf m-knopf-haupt"
              onClick={uebernehmen}
              disabled={laeuft || vorschau.aenderungen.length === 0}
            >
              {laeuft ? 'wird übernommen …' : `${vorschau.aenderungen.length} übernehmen`}
            </button>
          </>
        ) : undefined}
      >
        {vorschau && (
          <>
            <KpiReihe>
              <KpiCard titel="Zeilen gelesen" wert={vorschau.zeilenGelesen} />
              <KpiCard titel="Änderungen" wert={vorschau.aenderungen.length} />
              <KpiCard titel="Unverändert" wert={vorschau.unveraendert} />
              {vorschau.nichtZuordenbar.length > 0 && (
                <KpiCard
                  titel="Nicht zuordenbar" wert={vorschau.nichtZuordenbar.length}
                  zustand="fehler" zustandText="ohne Kennung oder unbekannt"
                />
              )}
            </KpiReihe>

            {vorschau.nichtZuordenbar.length > 0 && (
              <div className="hinweis warnung">
                <span className="hinweis-zeichen">!</span>
                <div>
                  <strong>Ohne Kennung oder unbekannt:</strong>{' '}
                  {vorschau.nichtZuordenbar.slice(0, 8).join(', ')}
                  {vorschau.nichtZuordenbar.length > 8 && ' …'}
                  <br />
                  <span className="sehr-schwach">
                    Die verborgene erste Spalte darf nicht gelöscht werden - über
                    sie laeuft die Zuordnung.
                  </span>
                </div>
              </div>
            )}

            {vorschau.auffaelligkeiten.length > 0 && (
              <div className="hinweis warnung">
                <span className="hinweis-zeichen">!</span>
                <div>
                  <strong>Nicht als Zahl lesbar:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {vorschau.auffaelligkeiten.slice(0, 6).map((a2, i) => (
                      <li key={i} style={{ fontSize: 12 }}>{a2}</li>
                    ))}
                  </ul>
                  <span className="sehr-schwach">Diese Werte werden übersprungen.</span>
                </div>
              </div>
            )}

            <DataTable
              name="Änderungen"
              spalten={aenderungsSpalten}
              zeilen={vorschau.aenderungen}
              schluessel={(a2) => `${a2.nummer}|${a2.feld}`}
              hoehe={420}
              dicht
              leer={(
                <EmptyState
                  titel="Keine Änderung."
                  erklaerung="Die Tabelle stimmt mit dem Raumbuch überein."
                />
              )}
            />
          </>
        )}
      </Drawer>
    </>
  );
}

/**
 * Fragt vor der PDF-Ausgabe, welcher Teil gebraucht wird — und sagt, was es
 * kostet.
 *
 * Bei tausend Räumen ist ein Buch von tausend Seiten unhandlich; meist geht es
 * um ein Geschoss oder einen Bauabschnitt.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM HIER EIN AUFTRAG STEHT UND KEIN ABRUF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Fachplaner am 23.09.2026: „der pdf export vom raumbuch startet aber
 * läuft dann ins timeout ohne was zu liefern." Der Abruf hing in EINER
 * Anfrage, und die endet am nginx nach 300 s. Gemessen (23.09.2026,
 * Bestandskopie): ein Geschoss mit 259 Räumen 18 s, alle 930 Räume 109 s.
 *
 * Der Dialog stößt deshalb einen Auftrag an, zeigt „n von N Räumen" und holt
 * die Datei ab, wenn sie fertig ist. Und er sagt VORHER, wie lange der letzte
 * gleiche Lauf gedauert hat — aus der Messung des Servers, nicht geschätzt.
 */
export function PdfDialog({
  api, projektId, schliessen,
}: {
  api: ApiClient;
  projektId: string;
  schliessen: () => void;
}) {
  const [daten, setDaten] = useState<RaumbuchDaten | null>(null);
  const [geschoss, setGeschoss] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [dauern, setDauern] = useState<RaumbuchDauer[]>([]);
  const [stand, setStand] = useState<RaumbuchAuftragStand | null>(null);

  useEffect(() => {
    api.raeume(projektId).then(setDaten).catch(() => setDaten(null));
    // Fehlt die Auskunft, steht im Dialog KEINE Dauer - und keine geschätzte.
    api.raumbuchPdfDauern(projektId).then(setDauern).catch(() => setDauern([]));
  }, [api, projektId]);

  const geschosse = daten
    ? [...new Set(daten.raeume
        .filter((r) => r.state !== 'entfernt')
        .map((r) => r.level)
        .filter((l): l is string => !!l))].sort((a, b) => a.localeCompare(b, 'de'))
    : [];

  const anzahl = daten
    ? daten.raeume.filter((r) => r.state !== 'entfernt'
        && (!geschoss || r.level === geschoss)).length
    : 0;

  const gemessen = dauern.find((d) => d.geschoss === geschoss);

  // Die Dauer für ALLE Räume - sie begründet den Hinweis, wenn kein Geschoss
  // gewählt ist. Steht sie nicht zur Verfügung, warnt der Hinweis trotzdem,
  // nur eben ohne Zahl.
  const dauerAlle = dauern.find((d) => d.geschoss === '');

  const erzeugen = async () => {
    setLaeuft(true);
    setFehler(null);
    setStand(null);

    try {
      const { kennung } = await api.raumbuchPdfAnstossen(
        projektId, geschoss || undefined);

      // Nachsehen, bis er fertig ist. Zwei Sekunden: Der Lauf dauert
      // Sekunden bis Minuten, und ein Abruf je 200 ms belastete den Server
      // mehr als die Erzeugung selbst.
      for (;;) {
        const s = await api.raumbuchPdfStand(projektId, kennung);
        setStand(s);

        if (s.zustand === 'gescheitert') {
          throw new Error(s.fehler || 'Das Raumbuch ist nicht entstanden.');
        }

        if (s.zustand === 'fertig') {
          await api.raumbuchPdfAbholen(
            projektId, kennung,
            geschoss ? `Raumbuch_${geschoss}.pdf` : 'Raumbuch.pdf');
          schliessen();
          return;
        }

        await new Promise((r) => { setTimeout(r, 2000); });
      }
    } catch (e) {
      setFehler(e);
      setLaeuft(false);
    }
  };

  const knopf = () => {
    if (!laeuft) {
      return gemessen
        ? `Erzeugen (zuletzt ${gemessen.sekunden} s)`
        : 'Erzeugen';
    }

    if (!stand) return 'wird angestoßen …';

    return stand.gesamt > 0 && stand.stufe === 'Grundrisse'
      ? `${stand.stufe}: ${stand.fertig} von ${stand.gesamt} Räumen …`
      : `${stand.stufe} …`;
  };

  return (
    <ConfirmDialog
      offen
      titel="Raumbuch als PDF"
      text={'Ein Blatt je Raum, mit allen gepflegten Angaben nach Fachgruppen '
        + 'geordnet. Felder ohne Wert bleiben aus.'}
      bestaetigenText={knopf()}
      laeuft={laeuft}
      fehler={fehler}
      // Ohne Raum gibt es nichts zu erzeugen; der Dialog bleibt aber mit ESC
      // schliessbar - `laeuft` waere dafuer das falsche Mittel.
      beiBestaetigen={() => { if (anzahl > 0) erzeugen(); }}
      beiAbbrechen={schliessen}
    >
      <MusterFeld
        art="auswahl"
        beschriftung="Umfang"
        wert={geschoss}
        beiAenderung={(w) => setGeschoss(w as string)}
        optionen={[
          { wert: '', beschriftung: 'alle Geschosse' },
          ...geschosse.map((g) => ({ wert: g, beschriftung: g })),
        ]}
        hinweis={daten
          ? `${anzahl} ${anzahl === 1 ? 'Raum' : 'Räume'} · ${anzahl + 1} Seiten`
            + (gemessen
                ? ` · zuletzt ${gemessen.geschoss || 'alle'} in ${gemessen.sekunden} s`
                : '')
          : 'wird ermittelt …'}
      />

      {/*
        OHNE GESCHOSS KEINE STUMME ANFRAGE. Das ganze Buch ist der Lauf, der
        beim Fachplaner ins Zeitlimit lief; hier steht deshalb vor dem Klick,
        was er kostet - mit der GEMESSENEN Dauer, wenn es eine gibt.
      */}
      {!geschoss && !laeuft && (
        <div className="hinweis warnung">
          <span className="hinweis-zeichen">!</span>
          <div>
            Alle {anzahl} Räume auf einmal
            {dauerAlle ? ` — zuletzt ${dauerAlle.sekunden} s` : ''}
            . Das Erzeugen läuft als Auftrag weiter, auch wenn es länger
            dauert; der Fortschritt steht hier. Für die Übergabe an Dritte ist
            ein einzelnes Geschoss meist handlicher
            {geschosse.length > 0 ? ` (zum Beispiel ${geschosse[0]})` : ''}.
          </div>
        </div>
      )}

      {laeuft && stand && (
        <div className="hinweis" role="status">
          <div>
            {stand.stufe}
            {stand.gesamt > 0 && stand.stufe === 'Grundrisse'
              ? `: ${stand.fertig} von ${stand.gesamt} Räumen`
              : ''}
            {' · '}
            {stand.sekunden} s
          </div>
        </div>
      )}
    </ConfirmDialog>
  );
}
